import React from 'react';

import { Link } from 'react-router-dom';

function WhyTheseStandardsExist() {
  return (
    <>

      <div className="bg-[#F9FAFB] min-h-screen">
        {/* Header Section */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-4xl mx-auto px-6 py-16 text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-[#102A43] mb-6">Why These Standards Exist</h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Addressing the critical need for verifiable accountability and operational baselines in the AI services market.
            </p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-6 py-12 space-y-12">
          
          {/* Section 1: The Current Gap */}
          <section className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
            <div className="bg-gradient-to-r from-blue-900 to-blue-800 px-8 py-4">
              <h2 className="text-xl font-bold text-white">1. The Current Gap</h2>
            </div>
            <div className="p-8 text-[#64748B] leading-relaxed">
              <p>
                As agencies rapidly adopt artificial intelligence to deliver services, a significant gap has emerged between capability and control. Clients are purchasing services delivered by autonomous or semi-autonomous agents without clear answers to fundamental questions: <em>Who is watching the system? Who is responsible when it errs? How is performance verified?</em>
              </p>
              <p className="mt-4">
                Without independent standards, these questions are often answered only after a failure occurs. The current market lacks a consistent framework for demonstrating that an agency has proactively addressed these risks.
              </p>
            </div>
          </section>

          {/* Section 2: Common Failure Patterns */}
          <section className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
            <div className="px-8 py-6 border-b border-gray-100">
              <h2 className="text-xl font-bold text-[#102A43]">2. Common Failure Patterns</h2>
            </div>
            <div className="p-8">
              <p className="text-[#64748B] mb-4">The standards were developed to address specific, recurring failure patterns in organization using AI operations:</p>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-red-500 mt-2 mr-3"></span>
                  <span className="text-[#64748B]"><strong>Undefined Accountability:</strong> Situations where responsibility is diffused, leading to "the AI did it" defenses rather than human ownership of outcomes.</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-red-500 mt-2 mr-3"></span>
                  <span className="text-[#64748B]"><strong>Systems Acting Beyond Scope:</strong> AI agents operating without defined boundaries, leading to unauthorized actions or communications.</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-red-500 mt-2 mr-3"></span>
                  <span className="text-[#64748B]"><strong>Reliance Before Responsibility:</strong> Agencies deploying tools they do not fully understand or monitor, creating dependency without governance.</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-red-500 mt-2 mr-3"></span>
                  <span className="text-[#64748B]"><strong>Lack of Evidence:</strong> The absence of audit trails or logs making it impossible to diagnose errors or prove due diligence.</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-red-500 mt-2 mr-3"></span>
                  <span className="text-[#64748B]"><strong>Governance Gaps:</strong> A general lack of policy regarding human oversight, leaving intervention decisions to ad-hoc judgment rather than protocol.</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Section 3: The Role of Baseline Controls */}
          <section className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
            <div className="px-8 py-6 border-b border-gray-100">
              <h2 className="text-xl font-bold text-[#102A43]">3. The Role of Baseline Controls</h2>
            </div>
            <div className="p-8 text-[#64748B] leading-relaxed">
              <p className="mb-4">
                AASC standards focus on <strong>preventive controls</strong>. We require agencies to define the "guard rails" before they deploy the system.
              </p>
              <p>
                To achieve declaration, an agency must demonstrate <strong>documented conformance</strong> to these controls. This means they must prove they have an escalation protocol for when the AI is unsure, a defined matrix of human responsibility for every AI output, and the technical ability to log and review agent actions. This moves safety from "best effort" to "standard operating procedure."
              </p>
            </div>
          </section>

          {/* Section 4: What AASC Is and Is Not */}
          <section className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
            <div className="px-8 py-6 border-b border-gray-100">
              <h2 className="text-xl font-bold text-[#102A43]">4. What AASC Is and Is Not</h2>
            </div>
            <div className="p-8 text-[#64748B] leading-relaxed">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="font-bold text-[#2F80ED] mb-2">What AASC Is</h3>
                  <ul className="list-disc pl-5 space-y-2 text-sm">
                    <li>An independent standards body.</li>
                    <li>A publisher of technical control frameworks.</li>
                    <li>A public verification registry.</li>
                    <li>A source for baseline guard rails.</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-bold text-[#102A43] mb-2">What AASC Is Not</h3>
                  <ul className="list-disc pl-5 space-y-2 text-sm">
                    <li>A government regulator.</li>
                    <li>A law firm providing legal advice.</li>
                    <li>A guarantee of error-free performance.</li>
                    <li>A substitute for regulatory compliance.</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Section 5: Final Statement */}
          <section className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
            <div className="px-8 py-6 border-b border-gray-100">
              <h2 className="text-xl font-bold text-[#102A43]">5. Final Statement</h2>
            </div>
            <div className="p-8 text-[#64748B] leading-relaxed">
              <p className="italic text-gray-600 mb-4 text-center">
                "declaration makes responsibility explicit."
              </p>
              <p>
                By adopting these standards, agencies signal that they are willing to be held accountable. They move from opaque operations to transparent, verifiable processes. This is the foundation of trust in the AI economy.
              </p>
            </div>
          </section>

          <div className="text-center pt-8 pb-4">
             <Link to="/tier-1-baseline-declaration" className="px-8 py-3 bg-[#2F80ED] text-white font-bold rounded-sm hover:bg-[#2563eb] transition-colors shadow-md inline-block">
               Explore Tier 1 Baseline declaration
             </Link>
          </div>

        </div>
      </div>
    </>
  );
}

export default WhyTheseStandardsExist;